import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The insurance surface the patient portal's booking flow needs, patient-scoped.
 *
 * `GET /insurance/patient/{id}/coverages` already answers the first question, but it returns FHIR
 * `Coverage` resources and takes the patient id from the path — neither is acceptable on a
 * patient-facing route. These read the caller's own record and expose only the fields the
 * "Use saved insurance" card shows.
 *
 * Under `/v2/patient/me/…` for the reason `patient-visits.contract.ts` spells out: a one-segment
 * sibling of `GET /v2/patient/{id}` is swallowed by it.
 */

/** Generous bound for a phone photo of an insurance card; the wire limit, not a business rule. */
export const INSURANCE_CARD_MAX_FILE_BYTES = 10 * 1024 * 1024

export const INSURANCE_CARD_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'] as const

export const insuranceCardImageSchema = z
  .file()
  .max(INSURANCE_CARD_MAX_FILE_BYTES)
  .mime([...INSURANCE_CARD_MIME_TYPES])

const insuranceRelationSchema = z.enum(['self', 'spouse', 'child', 'other'])

const savedCoverageSchema = z.object({
  coverageId: z.string(),
  /** `Primary` / `Secondary` / `Tertiary` — how the clinic ranked it. */
  rank: z.enum(['Primary', 'Secondary', 'Tertiary']),
  payerName: z.string().nullable(),
  memberId: z.string().nullable(),
  relation: insuranceRelationSchema.nullable(),
  subscriberName: z.string().nullable(),
  subscriberDob: z.string().nullable(),
  subscriberMemberId: z.string().nullable(),
  /** Card images already on file, so the patient is not asked to photograph them again. */
  cardFrontDocumentId: z.string().nullable(),
  cardBackDocumentId: z.string().nullable(),
})

const pendingInsuranceRequestSchema = z.object({
  requestId: z.string(),
  submittedAt: z.string().nullable(),
  payerName: z.string().nullable(),
  memberId: z.string().nullable(),
  relation: insuranceRelationSchema.nullable(),
  subscriberName: z.string().nullable(),
})

export const listPatientCoverages = oc
  .route({ method: 'GET', path: '/v2/patient/me/coverages' })
  .input(z.object({}))
  .output(
    z.object({
      items: z.array(savedCoverageSchema),
      pendingRequest: pendingInsuranceRequestSchema.nullable(),
    }),
  )

/**
 * Stores one side of an insurance card and returns the `DocumentReference` id to quote back to
 * `savePatientVisitInsurance`. Stateless and side-scoped, the same shape as the billing EOB
 * upload: the file travels as a real multipart part, which oRPC handles natively for `z.file()`.
 */
export const uploadPatientInsuranceCard = oc
  .route({ method: 'POST', path: '/v2/patient/me/insurance-card' })
  .input(
    z.object({
      side: z.enum(['front', 'back']),
      file: insuranceCardImageSchema,
    }),
  )
  .output(z.object({ documentId: z.string() }))

export const submitPatientInsurance = oc
  .route({ method: 'POST', path: '/v2/patient/me/insurance-request' })
  .input(
    z.object({
      idempotencyKey: z.string().min(1).max(64),
      payerName: z.string().min(1),
      payerId: z.string().optional(),
      relation: insuranceRelationSchema,
      memberId: z.string().min(1),
      subscriberName: z.string().optional(),
      subscriberDob: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'subscriberDob must be YYYY-MM-DD')
        .optional(),
      subscriberMemberId: z.string().optional(),
      frontFile: insuranceCardImageSchema,
      backFile: insuranceCardImageSchema.optional(),
    }),
  )
  .output(
    z.object({
      requestId: z.string(),
      cardFrontDocumentId: z.string(),
      cardBackDocumentId: z.string().nullable(),
    }),
  )

/** Also imported by the spec: Jest maps `@orpc/contract` to a mock with no `~orpc` accessor. */
export const savePatientVisitInsuranceInputSchema = z.object({
  encounterId: z.string().min(1),
  insurance: z.discriminatedUnion('mode', [
    z.object({
      mode: z.literal('saved'),
      coverageId: z.string().min(1),
    }),
    z.object({
      mode: z.literal('new'),
      payerName: z.string().min(1),
      /** Payer id from the catalog, when the patient picked a listed payer. */
      payerId: z.string().optional(),
      relation: insuranceRelationSchema,
      /** Optional: registration files the request even when the patient gave no member ID. */
      memberId: z.string().min(1).optional(),
      /** Required by the form whenever `relation` is not `self`. */
      subscriberName: z.string().optional(),
      subscriberDob: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'subscriberDob must be YYYY-MM-DD')
        .optional(),
      subscriberMemberId: z.string().optional(),
      cardFrontDocumentId: z.string().optional(),
      cardBackDocumentId: z.string().optional(),
    }),
  ]),
})

/**
 * Records which insurance covers one visit.
 *
 * Two answers, and they are stored differently — deliberately, because that is what the clinic's
 * workflow expects and what the old portal did:
 *
 * - `saved` attaches the existing `Coverage` to the appointment, which is a settled fact.
 * - `new` files a `patient-insurance-request` questionnaire response for staff to verify. It does
 *   *not* create a `Coverage`: an unverified plan the patient typed must not become billable
 *   coverage on its own.
 */
export const savePatientVisitInsurance = oc
  .route({ method: 'POST', path: '/v2/patient/me/visits/{encounterId}/insurance' })
  .input(savePatientVisitInsuranceInputSchema)
  .output(z.object({ success: z.boolean() }))
